/* eslint-disable @atlaskit/design-system/no-emotion-primitives -- Will migrate to @atlaskit/primitives/compiled in a follow-up. */
/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx -- Internal dev/admin UI strings; full i18n setup deferred to follow-up. */
/**
 * NoResultsHeaderNode - Custom ReactFlow node shown when a query returns no results.
 *
 * Displays a friendly message indicating the query produced no results, with
 * a subtitle suggesting options and a "Go back" button that undoes the last
 * action (restoring the previous query or landing page).
 *
 * @jsxRuntime classic
 * @jsx jsx
 */

import { memo, type MemoExoticComponent } from 'react';

import { type NodeProps, type Node } from '@xyflow/react';

import Button from '@atlaskit/button/new';
import { css, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export type NoResultsHeaderNodeData = {
	_tag: 'noResultsHeader';
	onGoBack: () => void;
};

export type NoResultsHeaderNodeType = Node<NoResultsHeaderNodeData, 'noResultsHeaderNode'>;

// ============================================================================
// Styles
// ============================================================================

const headerContainerStyles = css({
	pointerEvents: 'auto',
	userSelect: 'none',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.150'),
	textAlign: 'center',
});

const subtitleXcss = xcss({
	marginTop: 'space.050',
	maxWidth: '400px',
});

// ============================================================================
// NoResultsHeaderNode Component
// ============================================================================

export const NoResultsHeaderNode: MemoExoticComponent<
	(props: NodeProps<NoResultsHeaderNodeType>) => JSX.Element
> = memo((props: NodeProps<NoResultsHeaderNodeType>): JSX.Element => {
	const { onGoBack } = props.data;

	return (
		<div css={headerContainerStyles}>
			<Heading size="large">Couldn't find results for that query</Heading>
			<Box xcss={subtitleXcss}>
				<Text as="p" color="color.text.subtle">
					Try updating your query, or go back to your previous results
				</Text>
			</Box>
			<Button appearance="primary" onClick={onGoBack} testId="no-results-go-back">
				Go back
			</Button>
		</div>
	);
});

NoResultsHeaderNode.displayName = 'NoResultsHeaderNode';
